<template>
  <div class="sci-view-container">
    <!-- Top Glassmorphic Navigation -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button class="sci-btn-back" @click="home">
          <i class="fas fa-arrow-left"></i>
          <span>{{ $t('gestionale') || 'Gestionale' }}</span>
        </button>
        <div class="sci-title-group">
          <h1 class="sci-title">
            <i class="fas fa-robot me-2 text-primary"></i>
            {{ $t('report-ai') || 'AI Business Analyst & Copilot' }}
          </h1>
          <p class="sci-subtitle">Interroga l'intelligenza artificiale sui dati operativi, margini e performance del ristorante</p>
        </div>
      </div>
      <div class="sci-header-right">
        <span class="ai-status-pill">
          <span class="ai-pulse-dot"></span>
          <span>Gemini Intelligence Active</span>
        </span>
      </div>
    </header>

    <!-- Floating Feedback Toast -->
    <transition name="fade">
      <div class="sci-floating-toast" v-if="feedbackMsg">
        <i class="fas fa-check-circle text-success me-2"></i>
        <span>{{ feedbackMsg }}</span>
      </div>
    </transition>

    <!-- Main Spatial Bento Grid -->
    <main class="sci-main-content">
      <div class="ai-studio-grid">
        <!-- Sidebar: Storico Report -->
        <aside class="sci-card history-panel">
          <div class="history-header">
            <h2 class="history-title">
              <i class="fas fa-history me-2 text-primary"></i>
              Storico Report
            </h2>
            <span class="history-count-badge">{{ reports.length }}</span>
          </div>

          <div class="history-list" v-if="reports.length > 0">
            <div 
              v-for="rep in reports" 
              :key="rep._id" 
              class="history-item-card"
              :class="{ active: selectedReport && selectedReport._id === rep._id }"
              @click="selectReport(rep)"
            >
              <div class="history-item-content">
                <span class="history-date font-monospace">{{ formatDate(rep.created_at) }}</span>
                <p class="history-query-text">{{ rep.domanda }}</p>
              </div>

              <button 
                class="btn-delete-report" 
                title="Elimina Report" 
                @click.stop="confirmDelete(rep._id)"
              >
                <i class="fas fa-trash-alt"></i>
              </button>
            </div>
          </div>

          <div v-else class="empty-history text-center py-4">
            <i class="fas fa-comment-slash text-muted mb-2" style="font-size: 1.5rem;"></i>
            <p class="text-muted small m-0">Nessun report generato in archivio.</p>
          </div>
        </aside>

        <!-- Main Studio Area -->
        <section class="studio-main-panel">
          <!-- Suggested Quick Prompts -->
          <div class="quick-prompts-bar mb-3">
            <span class="quick-prompts-label">Suggerimenti Rapidi:</span>
            <div class="prompt-chips">
              <button 
                type="button" 
                class="prompt-chip" 
                v-for="(p, idx) in suggestedPrompts" 
                :key="idx"
                @click="applyPrompt(p)"
              >
                <i class="fas fa-sparkles me-1 text-primary"></i>
                {{ p }}
              </button>
            </div>
          </div>

          <!-- Prompt Input Bar -->
          <div class="sci-card prompt-card mb-4">
            <div class="prompt-input-row">
              <div class="prompt-input-wrap">
                <i class="fas fa-magic prompt-icon"></i>
                <input 
                  type="text" 
                  class="sci-prompt-input" 
                  v-model="query" 
                  placeholder="Chiedi all'IA (es. 'Analisi vendite piatti e margini del mese', 'Cosa ordinare per il magazzino')..."
                  @keyup.enter="sendQuery"
                  :disabled="loading"
                />
              </div>

              <button 
                class="sci-btn sci-btn-primary prompt-send-btn" 
                @click="sendQuery" 
                :disabled="loading || !query.trim()"
              >
                <i class="fas" :class="loading ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
                <span>{{ loading ? 'Analisi...' : 'Elabora Report' }}</span>
              </button>
            </div>
          </div>

          <!-- Report Result Display -->
          <div class="sci-card report-result-card">
            <!-- Loading Radar State -->
            <div v-if="loading" class="ai-loading-state text-center py-5">
              <div class="ai-radar-spinner">
                <div class="radar-circle"></div>
                <i class="fas fa-brain radar-icon"></i>
              </div>
              <h3 class="loading-title mt-4">Analisi Dati Operativi in Corso...</h3>
              <p class="loading-desc text-muted">
                L'intelligenza artificiale sta incrociando ordini, giacenze e marginalità per redigere il report.
              </p>
            </div>

            <!-- Content Render -->
            <div v-else-if="selectedReport" class="report-content-wrapper">
              <div class="report-meta-header">
                <div class="report-meta-left">
                  <span class="report-badge">Report Ufficiale AI</span>
                  <span class="report-timestamp font-monospace">{{ formatDate(selectedReport.created_at) }}</span>
                </div>
                <div class="report-actions">
                  <button class="sci-btn sci-btn-secondary btn-sm" @click="copyReport">
                    <i class="fas fa-copy me-1"></i> Copia Testo
                  </button>
                </div>
              </div>

              <div class="report-query-banner">
                <span class="query-lead">Q:</span>
                <span class="query-content">{{ selectedReport.domanda }}</span>
              </div>

              <div class="markdown-body" v-html="renderedReport"></div>
            </div>

            <!-- Welcome Empty State -->
            <div v-else class="welcome-ai-state text-center py-5">
              <div class="empty-icon-halo">
                <i class="fas fa-sparkles"></i>
              </div>
              <h3 class="empty-title">Benvenuto nell'assistente decisionale AI</h3>
              <p class="empty-desc">
                Poni una domanda o clicca su uno dei suggerimenti rapidi in alto per ottenere proiezioni finanziarie, analisi del menu e strategie per il tuo ristorante.
              </p>
            </div>
          </div>
        </section>
      </div>
    </main>
  </div>
</template>

<script>
import { marked } from 'marked'
import { store } from '../store/store.js'
import moment from 'moment'

export default {
  name: 'ReportAI',
  data() {
    return {
      store,
      query: '',
      loading: false,
      reports: [],
      selectedReport: null,
      feedbackMsg: '',
      suggestedPrompts: [
        "Analisi andamento vendite e margini del mese",
        "Quali piatti hanno la marginalità più alta?",
        "Suggerimenti per ridurre gli sprechi in magazzino",
        "Previsione affluenza e coperti per il prossimo weekend"
      ]
    }
  },
  computed: {
    renderedReport() {
      if (!this.selectedReport || !this.selectedReport.risposta) return '';
      const content = this.selectedReport.risposta;
      if (content.trim().startsWith('<')) {
        return content;
      }
      return marked(content);
    }
  },
  methods: {
    getToken() {
      try {
        if (!localStorage.token) return localStorage.getItem('token') || '';
        const parsed = JSON.parse(localStorage.token);
        return typeof parsed === 'string' ? parsed : (localStorage.getItem('token') || '');
      } catch (e) {
        return localStorage.getItem('token') || '';
      }
    },
    home() {
      this.$router.push({ name: "Gestionale" });
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      return moment(dateStr).format('DD/MM/YYYY HH:mm');
    },
    applyPrompt(prompt) {
      this.query = prompt;
      this.sendQuery();
    },
    selectReport(rep) {
      this.selectedReport = rep;
    },
    fetchReports() {
      const token = this.getToken();
      const authHeader = `Bearer ${token}`;

      this.axios.get(this.store.path + 'report-ai/reportsAI', {
        headers: { authorization: authHeader }
      }).then((response) => {
        if (response.data && response.data.messaggio) {
          this.reports = response.data.messaggio;
          if (this.reports.length > 0 && !this.selectedReport) {
            this.selectedReport = this.reports[0];
          }
        }
      }).catch(err => {
        console.error("Errore recupero report AI:", err);
      });
    },
    sendQuery() {
      if (!this.query.trim() || this.loading) return;
      this.loading = true;
      const question = this.query.trim();
      this.selectedReport = null;

      const token = this.getToken();
      const authHeader = `Bearer ${token}`;

      this.axios.post(this.store.path + 'report-ai/queryAI', {
        domanda: question
      }, {
        headers: { authorization: authHeader },
        timeout: 300000
      }).then((response) => {
        if (response.data && response.data.report) {
          this.selectedReport = response.data.report;
          this.reports.unshift(response.data.report);
          this.query = '';
          this.showToast('Report AI generato con successo!');
        }
      }).catch(err => {
        console.error("Errore interrogazione AI:", err);
        alert("Si è verificato un errore durante l'elaborazione del report AI.");
      }).finally(() => {
        this.loading = false;
      });
    },
    confirmDelete(id) {
      if (!confirm("Sei sicuro di voler eliminare questo report salvato?")) {
        return;
      }

      const token = this.getToken();
      const authHeader = `Bearer ${token}`;

      this.axios.delete(this.store.path + `report-ai/deleteReport/${id}`, {
        headers: { authorization: authHeader }
      }).then(() => {
        this.reports = this.reports.filter(r => r._id !== id);
        if (this.selectedReport && this.selectedReport._id === id) {
          this.selectedReport = this.reports.length > 0 ? this.reports[0] : null;
        }
        this.showToast('Report eliminato con successo');
      }).catch(err => {
        console.error("Errore cancellazione report:", err);
      });
    },
    copyReport() {
      if (!this.selectedReport) return;
      navigator.clipboard.writeText(this.selectedReport.risposta).then(() => {
        this.showToast('Testo del report copiato negli appunti');
      }).catch(() => {
        alert('Impossibile copiare negli appunti');
      });
    },
    showToast(msg) {
      this.feedbackMsg = msg;
      setTimeout(() => {
        if (this.feedbackMsg === msg) this.feedbackMsg = '';
      }, 2500);
    }
  },
  mounted() {
    this.fetchReports();
  }
}
</script>

<style scoped>
.sci-view-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sci-bg, #f8fafc);
  color: var(--sci-text-main, #0f172a);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 60px;
}

/* Header */
.sci-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 18px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 12px;
  color: var(--sci-text-muted, #475569);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}

.sci-btn-back:hover {
  background: #f1f5f9;
  color: var(--sci-text-main, #0f172a);
  border-color: #cbd5e1;
}

.sci-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
}

.sci-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.82rem;
  color: var(--sci-text-muted, #475569);
}

.ai-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 20px;
  background: #eef2ff;
  color: var(--sci-primary, #4f46e5);
  font-size: 0.82rem;
  font-weight: 700;
  border: 1px solid #c7d2fe;
}

.ai-pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
}

.sci-main-content {
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
  padding: 24px 20px;
}

/* Studio Layout */
.ai-studio-grid {
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 24px;
  align-items: start;
}

@media (max-width: 992px) {
  .ai-studio-grid {
    grid-template-columns: 1fr;
  }
}

/* Card */
.sci-card {
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 18px;
  box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.05);
}

/* History Panel */
.history-panel {
  padding: 20px;
  max-height: calc(100vh - 160px);
  display: flex;
  flex-direction: column;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
}

.history-title {
  font-size: 1.05rem;
  font-weight: 700;
  margin: 0;
}

.history-count-badge {
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #475569;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  padding-right: 4px;
}

.history-item-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.history-item-card:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.history-item-card.active {
  background: #eef2ff;
  border-color: #c7d2fe;
}

.history-date {
  font-size: 0.72rem;
  color: var(--sci-text-muted, #475569);
  display: block;
}

.history-query-text {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--sci-text-main, #0f172a);
  margin: 4px 0 0 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.btn-delete-report {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: color 0.2s;
}

.btn-delete-report:hover {
  color: #ef4444;
}

/* Quick Prompts */
.quick-prompts-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.quick-prompts-label {
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--sci-text-muted, #475569);
}

.prompt-chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.prompt-chip {
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--sci-text-muted, #475569);
  cursor: pointer;
  transition: all 0.2s;
}

.prompt-chip:hover {
  background: #eef2ff;
  border-color: #c7d2fe;
  color: var(--sci-primary, #4f46e5);
}

/* Prompt Card */
.prompt-card {
  padding: 16px;
}

.prompt-input-row {
  display: flex;
  gap: 12px;
  align-items: center;
}

.prompt-input-wrap {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
}

.prompt-icon {
  position: absolute;
  left: 16px;
  color: var(--sci-primary, #4f46e5);
  font-size: 1.1rem;
}

.sci-prompt-input {
  width: 100%;
  height: 48px;
  padding: 0 16px 0 46px;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 12px;
  font-size: 0.95rem;
  color: var(--sci-text-main, #0f172a);
  background: #f8fafc;
  outline: none;
  transition: all 0.2s;
}

.sci-prompt-input:focus {
  background: #ffffff;
  border-color: var(--sci-primary, #4f46e5);
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.prompt-send-btn {
  height: 48px;
  padding: 0 24px;
  border-radius: 12px;
  font-weight: 700;
  gap: 8px;
  display: inline-flex;
  align-items: center;
}

/* Report Result */
.report-result-card {
  padding: 28px;
  min-height: 500px;
}

.report-meta-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 14px;
  margin-bottom: 18px;
}

.report-badge {
  background: #eef2ff;
  color: #4f46e5;
  font-weight: 700;
  font-size: 0.8rem;
  padding: 4px 10px;
  border-radius: 6px;
  margin-right: 10px;
}

.report-timestamp {
  font-size: 0.82rem;
  color: var(--sci-text-muted, #475569);
}

.report-query-banner {
  background: #f8fafc;
  border-left: 4px solid var(--sci-primary, #4f46e5);
  padding: 12px 16px;
  border-radius: 0 10px 10px 0;
  margin-bottom: 24px;
  font-size: 0.95rem;
}

.query-lead {
  font-weight: 800;
  color: var(--sci-primary, #4f46e5);
  margin-right: 8px;
}

.query-content {
  font-weight: 600;
  color: var(--sci-text-main, #0f172a);
}

/* Markdown typography */
.markdown-body {
  font-size: 0.95rem;
  line-height: 1.65;
  color: #1e293b;
}

.markdown-body :deep(h1), 
.markdown-body :deep(h2), 
.markdown-body :deep(h3) {
  margin-top: 24px;
  margin-bottom: 12px;
  font-weight: 700;
  color: #0f172a;
}

.markdown-body :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
}

.markdown-body :deep(th), 
.markdown-body :deep(td) {
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
}

.markdown-body :deep(th) {
  background: #f8fafc;
  font-weight: 700;
}

.markdown-body :deep(ul), 
.markdown-body :deep(ol) {
  padding-left: 20px;
}

/* Radar Spinner */
.ai-radar-spinner {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.radar-circle {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid rgba(79, 70, 229, 0.2);
  border-top-color: var(--sci-primary, #4f46e5);
  animation: spin 1s infinite linear;
}

.radar-icon {
  font-size: 2rem;
  color: var(--sci-primary, #4f46e5);
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-icon-halo {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #eef2ff;
  color: var(--sci-primary, #4f46e5);
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px auto;
}

.empty-title {
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 6px;
}

.empty-desc {
  font-size: 0.9rem;
  color: var(--sci-text-muted, #475569);
  max-width: 520px;
  margin: 0 auto;
}

.sci-btn-secondary {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 8px;
  color: var(--sci-text-main, #0f172a);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.sci-btn-secondary:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.sci-floating-toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
  display: flex;
  align-items: center;
  background: #0f172a;
  color: #ffffff;
  padding: 12px 20px;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  font-weight: 600;
  font-size: 0.9rem;
}
</style>
